import type { ReportDetail } from '../../../api/DigiobcanApi';
import type { IFeedItem } from '../../../components/FeedItem/FeedItem';
import { meGetTargetVoteTokens } from '../../../features/queries/meGetTargetVoteTokens';
import { meGetVoteTokensSummary } from '../../../features/queries/meGetVoteTokensSummary';
import { entityId } from '../../../lib/ids';
import { QueryNameSpace, useQ } from '../../../lib/query';
import { __ } from '../../../utils';
import VoteButton from './VoteButton';
import { Stack, useTheme, useMediaQuery } from '@presov.sk/presov-ui-react';
import { useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router';

type Props = {
  /** Počet hlasov na podnete cez všetky účty. */
  votesCount: number;
  onClickUnregisteredUser?: () => void;
  /** Podnet mimo route `/podnety/:reportId` - viď [[VoteButton]]. */
  reportId?: string;
  variant?: React.ComponentProps<typeof VoteButton>['variant'];
};

/** Jedna stránka feedu tak, ako ju do cache ukladá [[getFeed]]. */
type FeedPage = { items: IFeedItem[] };
type FeedData = { pages: FeedPage[] } | undefined;

const VoteCardActions: React.FC<Props> = ({
  votesCount,
  onClickUnregisteredUser,
  reportId: reportIdProp,
  variant,
}) => {
  const { reportId: reportIdParam } = useParams() as { reportId?: string };
  const reportId = reportIdProp ?? entityId(reportIdParam ?? '', 'report');
  const queryClient = useQueryClient();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const {
    data: voteTokensPool,
    isLoading: isLoadingVoteTokensPool,
    queryKey: queryKeyVoteTokensPool,
  } = useQ(meGetVoteTokensSummary);
  const {
    data: targetVoteTokens,
    isLoading: isLoadingTargetVoteTokens,
    queryKey: queryKeyTargetVoteTokens,
  } = useQ(meGetTargetVoteTokens, { target: reportId });

  const updateQueries = async (isVoting: boolean) => {
    const delta = isVoting ? 1 : -1;

    // Fond žetónov občana: hlas ide z `available` do `active` a naopak.
    queryClient.setQueryData(
      queryKeyVoteTokensPool,
      (oldData: typeof voteTokensPool) => {
        if (!oldData) return oldData;
        return {
          ...oldData,
          active_tokens: (oldData.active_tokens || 0) + delta,
          available_tokens: (oldData.available_tokens || 0) - delta,
        };
      },
    );

    // Ako občan stojí na tomto podnete - z toho sa riadi zapnutie tlačidla.
    queryClient.setQueryData(
      queryKeyTargetVoteTokens,
      (oldData: typeof targetVoteTokens) => {
        if (!oldData) return oldData;
        return {
          ...oldData,
          active_tokens: (oldData.active_tokens || 0) + delta,
          available_tokens: (oldData.available_tokens || 0) - delta,
        };
      },
    );

    // Počet hlasov na podnete, teda číslo v tlačidle. Sedí **v dvoch cache**
    // a dovtedy sa prepisovala len jedna: detail podnetu. Panel na hlavnej
    // stránke aj karty vo feede berú `votesCount` z [[getFeed]], takže tam
    // číslo po hlasovaní stálo na mieste - tlačidlo sa preplo a nič viac.
    queryClient.setQueryData(
      [QueryNameSpace.getReportDetail, { reportId }],
      (oldData: ReportDetail) => {
        if (!oldData || typeof oldData !== 'object') return oldData;
        return {
          ...oldData,
          votes_count: (oldData.votes_count || 0) + delta,
        };
      },
    );

    // Feed je nekonečný dotaz s viacerými variantmi parametrov (segment,
    // poloha, zoradenie), takže sa prepisujú **všetky** jeho stránky podľa
    // predpony kľúča - nie jeden konkrétny kľúč, ktorý by sa tu musel skladať
    // znova a pri každej zmene filtra rozísť.
    queryClient.setQueriesData(
      { queryKey: [QueryNameSpace.getFeed] },
      (oldData: FeedData) => {
        if (!oldData?.pages) return oldData;

        return {
          ...oldData,
          pages: oldData.pages.map((page) => ({
            ...page,
            items: (page.items ?? []).map((item) =>
              item.id === reportId
                ? { ...item, votesCount: (item.votesCount ?? 0) + delta }
                : item,
            ),
          })),
        };
      },
    );

    // Optimistický prepis je odhad; skutočné číslo povie server. Detail sa
    // preto ešte dotiahne - `count` na `/me/vote-tokens` nie je viazané na
    // jeden hlas a medzitým mohol podnet podporiť aj niekto iný.
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: [QueryNameSpace.getReportDetail, { reportId }],
      }),
      // Aktualizujeme hlasovania v profile
      queryClient.refetchQueries({
        queryKey: [QueryNameSpace.meGetVoteTokens],
      }),
    ]);
  };

  const isLoading = isLoadingVoteTokensPool || isLoadingTargetVoteTokens;

  return (
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      alignItems={'center'}
      justifyContent={'start'}
      spacing={2}
      width='100%'
      sx={{
        '> div': {
          width: isMobile ? '100%' : 'auto',
        },
      }}
    >
      <VoteButton
        reportId={reportId}
        variant={variant}
        isLoading={isLoading}
        onClickUnregisteredUser={onClickUnregisteredUser}
        userAvailableVoteTokens={voteTokensPool?.available_tokens || 0}
        userActiveVoteTokens={targetVoteTokens?.active_tokens || 0}
        globalVoteTokens={votesCount}
        onSuccess={updateQueries}
        fullWidth={variant === 'panel' || isMobile}
        tooltip={__('Chcem, aby sa tejto záležitosti venovala pozornosť.')}
      />
    </Stack>
  );
};

export default VoteCardActions;
